import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { m, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { 
  ShieldCheck, 
  Truck, 
  Star, 
  Clock, 
  ArrowRight, 
  Check,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface PromoPromoSectionProps {
  products: any[];
  config: {
    title?: string;
    subtitle?: string;
    countdown_end?: string;
    show_badges?: boolean;
    layout_style?: "carousel" | "grid";
    cta_text?: string;
  };
}

export const PromoPromoSection = ({ products, config }: PromoPromoSectionProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0, days: 0 });
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!config.countdown_end) return;

    const calculateTimeLeft = () => {
      const difference = +new Date(config.countdown_end!) - +new Date();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    };

    const timer = setInterval(calculateTimeLeft, 1000);
    calculateTimeLeft();

    return () => clearInterval(timer);
  }, [config.countdown_end]);

  if (!products || products.length === 0) return null;

  const trustBadges = [
    { icon: ShieldCheck, text: "Certified Devices", color: "text-blue-600", bg: "bg-blue-50" },
    { icon: Truck, text: "Free Delivery", color: "text-emerald-600", bg: "bg-emerald-50" },
    { icon: Star, text: "4.9/5 Rating", color: "text-amber-600", bg: "bg-amber-50" },
  ];

  return (
    <section className="relative py-20 sm:py-28 overflow-hidden bg-white">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[10%] -left-[5%] w-[40%] h-[40%] rounded-full bg-blue-50/50 blur-[120px] animate-pulse" />
        <div className="absolute top-[20%] -right-[5%] w-[30%] h-[30%] rounded-full bg-indigo-50/40 blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Top Header Area with Badges & Countdown */}
        <div className="flex flex-col items-center mb-16 text-center">
          {/* Trust Badges */}
          <AnimatePresence mode="wait">
            {config.show_badges !== false && (
              <m.div 
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex flex-wrap justify-center gap-4 sm:gap-8 mb-10"
              >
                {trustBadges.map((badge, idx) => (
                  <m.div 
                    key={idx}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex items-center gap-2.5 px-4 py-2 rounded-full border border-gray-100 bg-white/50 backdrop-blur-sm shadow-sm"
                  >
                    <div className={`w-8 h-8 rounded-full ${badge.bg} flex items-center justify-center`}>
                      <badge.icon className={`w-4 h-4 ${badge.color}`} />
                    </div>
                    <span className="text-sm font-bold text-gray-700">{badge.text}</span>
                  </m.div>
                ))}
              </m.div>
            )}
          </AnimatePresence>

          {/* Title & Subtitle */}
          <m.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-black text-gray-900 mb-4 tracking-tight">
              {config.title || "Limited Offer"}
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto font-medium">
              {config.subtitle || "Exclusive deals on our most popular devices."}
            </p>
          </m.div>

          {/* Countdown Timer Card */}
          <m.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="flex flex-col sm:flex-row items-center gap-4 bg-gray-900 text-white px-8 py-4 rounded-[2rem] shadow-xl shadow-blue-900/10 border border-white/10">
              <div className="flex items-center gap-2 text-blue-400 font-black uppercase tracking-widest text-[10px]">
                <Clock className="w-4 h-4 animate-spin-slow" />
                Ends In:
              </div>
              <div className="flex items-center gap-4">
                {[
                  { label: "HRS", value: timeLeft.hours + (timeLeft.days * 24) },
                  { label: "MIN", value: timeLeft.minutes },
                  { label: "SEC", value: timeLeft.seconds }
                ].map((unit, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="flex flex-col items-center">
                      <span className="text-3xl sm:text-4xl font-mono font-black tabular-nums">
                        {String(unit.value).padStart(2, '0')}
                      </span>
                      <span className="text-[9px] font-bold text-gray-500 uppercase tracking-tighter mt-1">{unit.label}</span>
                    </div>
                    {i < 2 && <span className="text-2xl font-black text-gray-700 pb-5">:</span>}
                  </div>
                ))}
              </div>
            </div>
            <div className="absolute -top-3 -right-3 w-6 h-6 bg-blue-600 rounded-full animate-ping opacity-20" />
          </m.div>
        </div>

        {/* Product Display Area */}
        <div className="relative mt-8">
          {config.layout_style === "carousel" && products.length > 1 ? (
            <div className="relative px-4 sm:px-12">
              <div className="overflow-hidden rounded-[2.5rem]">
                <m.div 
                  className="flex gap-6"
                  animate={{ x: `calc(-${activeIndex * 100}% - ${activeIndex * 24}px)` }}
                  transition={{ type: "spring", damping: 25, stiffness: 120 }}
                >
                  {products.map((product, idx) => (
                    <div key={product.id} className="min-w-full lg:min-w-[calc(50%-12px)] flex-shrink-0">
                      <PromoProductCard product={product} ctaText={config.cta_text} />
                    </div>
                  ))}
                </m.div>
              </div>

              {/* Navigation Controls */}
              <div className="hidden sm:flex absolute top-1/2 -translate-y-1/2 left-0 right-0 justify-between pointer-events-none px-2 text-blue-100">
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="w-12 h-12 rounded-full bg-white/80 backdrop-blur-md border hover:bg-blue-600 hover:text-white pointer-events-auto shadow-lg"
                  onClick={() => setActiveIndex(prev => Math.max(0, prev - 1))}
                  disabled={activeIndex === 0}
                >
                  <ChevronLeft className="w-6 h-6" />
                </Button>
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="w-12 h-12 rounded-full bg-white/80 backdrop-blur-md border hover:bg-blue-600 hover:text-white pointer-events-auto shadow-lg"
                  onClick={() => setActiveIndex(prev => Math.min(products.length - (isMobile ? 1 : 2), prev + 1))}
                  disabled={activeIndex >= products.length - (isMobile ? 1 : 2)}
                >
                  <ChevronRight className="w-6 h-6" />
                </Button>
              </div>
            </div>
          ) : (
            <div className={`grid gap-8 ${products.length === 1 ? 'max-w-3xl mx-auto' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
              {products.map((product) => (
                <PromoProductCard key={product.id} product={product} ctaText={config.cta_text} isLarge={products.length === 1} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const PromoProductCard = ({ product, ctaText, isLarge = false }: { product: any; ctaText?: string; isLarge?: boolean }) => {
  const discount = product.original_price && product.original_price > product.price 
    ? Math.round((1 - product.price / product.original_price) * 100) 
    : null;

  return (
    <m.div 
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -10 }}
      transition={{ duration: 0.4 }}
      className={`group relative overflow-hidden rounded-[2.5rem] border border-gray-100 bg-white p-6 transition-all hover:shadow-2xl hover:shadow-blue-900/10 ${isLarge ? 'md:p-12' : ''}`}
    >
      <div className={`relative flex flex-col ${isLarge ? 'md:flex-row md:items-center md:gap-12' : ''}`}>
        
        {/* Product Image Area */}
        <div className={`relative mb-8 overflow-hidden rounded-[2rem] bg-gray-50 p-6 flex items-center justify-center ${isLarge ? 'md:w-1/2 md:mb-0' : 'aspect-square'}`}>
          <m.img 
            src={product.image_url || "/placeholder.svg"} 
            alt={product.name}
            className="w-full h-full object-contain mix-blend-multiply group-hover:scale-110 transition-transform duration-700 ease-out"
          />
          
          {/* Badge overlays */}
          {discount && (
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              <div className="bg-red-600 text-white text-[10px] sm:text-xs font-black px-3 py-1.5 rounded-full shadow-lg shadow-red-600/20 uppercase tracking-widest flex items-center gap-1.5 animate-bounce">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                {discount}% OFF
              </div>
            </div>
          )}
          
          <div className="absolute top-4 right-4">
            <div className="bg-white/80 backdrop-blur-md border border-white p-2 rounded-2xl shadow-sm">
              <Check className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Product Details Area */}
        <div className={`${isLarge ? 'md:w-1/2' : ''}`}>
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
               <span className="px-3 py-1 rounded-full bg-blue-50 text-[10px] font-black text-blue-600 uppercase tracking-wider">{product.brand}</span>
               <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Premium Choice</span>
            </div>
            <h3 className={`font-display font-black text-gray-900 tracking-tight leading-tight group-hover:text-blue-600 transition-colors ${isLarge ? 'text-3xl sm:text-4xl' : 'text-xl sm:text-2xl'}`}>
              {product.name}
            </h3>
          </div>

          <div className="mb-8 p-5 rounded-[1.5rem] bg-gray-50/50 border border-gray-100/50">
            <div className="flex items-baseline gap-3 mb-1">
              <span className={`font-black text-gray-900 ${isLarge ? 'text-4xl' : 'text-3xl'}`}>
                {Number(product.price).toLocaleString()} <span className="text-sm">DH</span>
              </span>
              {product.original_price && (
                <span className="text-gray-400 line-through font-bold text-sm">
                  {Number(product.original_price).toLocaleString()} DH
                </span>
              )}
            </div>
            <p className="text-xs text-emerald-600 font-bold flex items-center gap-1.5">
              <Truck className="w-3.5 h-3.5" /> Fast Delivery in 24-48h Available
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button size="lg" className="w-full sm:flex-1 h-14 rounded-2xl bg-gray-900 hover:bg-blue-600 text-white font-bold group/btn shadow-xl shadow-gray-900/10 transition-all" asChild>
              <Link to={`/product/${product.slug}`}>
                {ctaText || "View Deal"}
                <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" className="w-full sm:flex-1 h-14 rounded-2xl border-2 font-bold px-8 hover:bg-blue-50 transition-colors" asChild>
              <Link to="/cart">Add To Cart</Link>
            </Button>
          </div>

          <div className="mt-8 flex items-center justify-center sm:justify-start gap-3 sm:gap-6 text-[9px] sm:text-[10px] font-black text-gray-400/80 uppercase tracking-widest overflow-hidden">
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gray-50/50 border border-gray-100/50 transition-colors hover:bg-gray-100">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
              12 Months Warranty
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gray-50/50 border border-gray-100/50 transition-colors hover:bg-gray-100">
              <Star className="w-3.5 h-3.5 text-amber-500" />
              Verified Passport
            </div>
          </div>
        </div>

      </div>
    </m.div>
  );
};
